import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import _ from 'lodash'
import { io } from 'socket.io-client'
import {
  resetState,
  setIsClassEndFlag,
} from 'src/redux/slices/eLearning/learning'
import { COURSE_TYPE, MODULE_TYPE } from 'src/constants/eLearning'
import { getQueryParams } from 'src/utils/lib'
import FloatingButtons from './components/FloatingButtons'
import LeftPanel from './LeftPanel'
import RightPanel from './RightPanel'
import CameraContent from './CameraDialog'
import ClickDialog from './ClickDialog'
import LearningAlertDialog from './AlertDialog'
import { initialDialog } from './AlertDialog/model'
import { StyledContainer } from './Styled'
import {
  detectUsableBrowser,
  handleInitLearning,
  handleSocketResponse,
} from './events'
import updateTimeSpentlog from './handler/updateTimeSpentLog'
import clearTimeSpentLogInterval from './handler/clearTimeSpentLogInterval'
import initialTimeSpentLog from './handler/initialTimeSpentLog'
import { useWindowFocus } from './hooks/useWindowFocus'
import handleWindowFocus from './handler/handleWindowFocus'
import dayjs from 'dayjs'
import ClassEnd from './components/ClassEnd'
import { Column } from 'src/components/Layout/Styled'

const { __env__: env } = window

const socket = io.connect(env.REACT_APP_SOCKET_URL, {
  autoConnect: false,
  transports: ['websocket', 'polling'],
  auth: {
    Authorization: localStorage.getItem('token'),
  },
})

const Learning = () => {
  const dispatch = useDispatch()
  const { id: uuid } = useParams()
  const batchUuid = getQueryParams('batch')
  const {
    course,
    INTERVAL_TIME,
    MAXIMUM_RETRY,
    timeSpentLogUuid,
    selectedLesson,
    isClassEndFlag,
  } = useSelector(
    (state) => ({
      course: state.eLearningLearning.course,
      INTERVAL_TIME: state.eLearningLearning.INTERVAL_TIME,
      MAXIMUM_RETRY: state.eLearningLearning.MAXIMUM_RETRY,
      timeSpentLogUuid: state.eLearningLearning.timeSpentLogUuid,
      selectedLesson: state.eLearningLearning.selectedLesson,
      isClassEndFlag: state.eLearningLearning.isClassEndFlag,
    }),
    shallowEqual
  )
  const courseType = _.get(course, 'courseType', '')
  const isOic = courseType === COURSE_TYPE.OIC
  const [socketId, setSocketId] = useState('')
  const uuidProps = { uuid, batchUuid }
  const [alertDialog, setAlertDialog] = useState({ ...initialDialog })
  const [socketDialog, setSocketDialog] = useState({ ...initialDialog })
  const dialogProps = { setAlertDialog, setSocketDialog }
  const isTabFocus = useWindowFocus()
  const isUsableBrowser = detectUsableBrowser()
  const [endClassInterval, setEndClassInterval] = useState('')

  /* ------ socket handling ------- */
  useEffect(() => {
    socket.connect()

    return () => {
      dispatch(resetState())
      socket.disconnect()
    }
  }, [])

  useEffect(() => {
    const onConnect = () => {
      console.log('socket connected', socket.connected)
      if (socket.connected) {
        setSocketId(socket.id)
        socket.on(socket.id, (res) => {
          dispatch(handleSocketResponse(res, socket, uuidProps, dialogProps))
        })
      }
    }

    const onDisconnect = () => socket.disconnect()

    socket.on('connect', onConnect)
    socket.on('disconnect', onDisconnect)
    return () => {
      socket.off('connect', onConnect)
      socket.off('disconnect', onDisconnect)
    }
  }, [])

  useEffect(() => {
    dispatch(handleInitLearning(socketId, uuidProps))
  }, [socketId])
  /* ------ socket handling ------- */

  useEffect(() => {
    if (env.ENABLE_OIC_EL !== 'true') return
    if (
      [MODULE_TYPE.EMBED_CONTENT].includes(selectedLesson.moduleType) &&
      !isUsableBrowser
    )
      return

    // interval update log time spent and log learner
    if (
      !_.isEmpty(MAXIMUM_RETRY) &&
      !_.isEmpty(INTERVAL_TIME) &&
      !_.isEmpty(timeSpentLogUuid) &&
      [MODULE_TYPE.EMBED_CONTENT, MODULE_TYPE.SCORM].includes(
        selectedLesson.moduleType
      )
    ) {
      const interval = setInterval(
        () => dispatch(updateTimeSpentlog()),
        INTERVAL_TIME * 1000
      )
      localStorage.setItem('updateIntervalId', interval)
    } else {
      clearTimeSpentLogInterval('updateIntervalId')
    }
    return () => {
      clearTimeSpentLogInterval()
    }
  }, [MAXIMUM_RETRY, INTERVAL_TIME, timeSpentLogUuid])

  useEffect(() => {
    /* create new time spent log
       on first fetch and switch module */
    dispatch(initialTimeSpentLog())
  }, [selectedLesson.uuid])

  useEffect(() => {
    if (env.ENABLE_OIC_EL !== 'true') return
    if (
      [MODULE_TYPE.EMBED_CONTENT].includes(selectedLesson.moduleType) &&
      !isUsableBrowser
    )
      return

    // handle focus and unfocus tab
    dispatch(handleWindowFocus(isTabFocus))
  }, [isTabFocus])

  useEffect(() => {
    const periodEnd = _.get(
      course,
      'eLearningCourseBatchLearner.eLearningCourseBatch.periodEnd',
      ''
    )
    if (periodEnd) {
      const interval = setInterval(() => {
        dispatch(setIsClassEndFlag(dayjs() > dayjs(`${periodEnd} 23:59:59`)))
      }, 10000)
      setEndClassInterval(interval)
    }
    return () => {
      clearInterval(endClassInterval)
    }
  }, [course])

  if (_.isEmpty(course)) return <></>

  return (
    <Box sx={{ position: 'relative' }}>
      {isOic && (
        <>
          <CameraContent />
          <ClickDialog />
        </>
      )}

      <StyledContainer>
        <FloatingButtons />

        {isClassEndFlag ? (
          <ClassEnd />
        ) : [MODULE_TYPE.EMBED_CONTENT].includes(selectedLesson.moduleType) &&
          !isUsableBrowser ? (
          <Column sx={{ my: 'auto', mx: 4 }}>
            <Typography
              variant="h5"
              color="primary"
              sx={{ textAlign: 'center' }}
            >
              เพื่อประสบการณ์การเรียนรู้ที่ดีที่สุด กรุณาใช้เว็บเบราว์เซอร์
              Chrome, MS Edge, Safari หรือ Firefox เท่านั้น
            </Typography>
          </Column>
        ) : (
          <>
            <LeftPanel />
            <RightPanel setAlertDialog={setAlertDialog} />
          </>
        )}
      </StyledContainer>

      <LearningAlertDialog
        alertDialog={socketDialog}
        setAlertDialog={setSocketDialog}
      />
      <LearningAlertDialog
        alertDialog={alertDialog}
        setAlertDialog={setAlertDialog}
      />
    </Box>
  )
}

export default Learning
